import React from 'react';
import {
  Box, Typography, Paper, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Divider, Stack,
  Accordion, AccordionSummary, AccordionDetails,
  useTheme, alpha,
} from '@mui/material';
import {
  CheckCircleOutline, ExpandMore, CancelOutlined,
  CalendarMonth, HourglassBottom, AutorenewOutlined,
} from '@mui/icons-material';
import { ProductPlan } from 'modules/Interfaces/Company/Company';
import { Company } from 'modules/Interfaces/Company/Company';
import {
  BusinessPlanFromAPI, buildConfigRows, formatDate,
} from './types';


interface CurrentPlanSectionProps {
  company: Company;
  currentPlanKey: string;
  currentPlanDef: BusinessPlanFromAPI | null;
  hasAnnualCommitment: boolean;
  activePlan: ProductPlan | null;
  isAnnual: boolean;
  nextRenewalDate: string;
  autoRenewalCancelled: boolean;
  onShowCancelConfirm: () => void;
  onShowRenewConfirm: () => void;
}

export const CurrentPlanSection: React.FC<CurrentPlanSectionProps> = ({
  company, currentPlanKey, currentPlanDef, hasAnnualCommitment,
  activePlan, isAnnual, nextRenewalDate,
  autoRenewalCancelled, onShowCancelConfirm, onShowRenewConfirm,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBg = isDark ? alpha(theme.palette.common.black, 0.35) : theme.palette.common.white;
  const panelText = isDark ? theme.palette.common.white : theme.palette.text.primary;
  const panelTextSecondary = isDark ? alpha(theme.palette.common.white, 0.7) : theme.palette.text.secondary;
  const dividerColor = isDark ? alpha(theme.palette.common.white, 0.15) : theme.palette.divider;
  const accordionBg = isDark ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.03);
  const tableHeaderBg = isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.04);
  const tableBorder = isDark ? alpha(theme.palette.common.white, 0.1) : theme.palette.divider;
  const tableBorderLight = isDark ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.06);
  const valueColor = isDark ? '#81c784' : '#2e7d32';
  const commitChipColor = isDark ? '#90caf9' : theme.palette.info.main;
  const licenseChipColor = isDark ? '#ffd166' : '#b8860b';
  const usersBadgeBg = isDark ? alpha(theme.palette.info.main, 0.25) : alpha(theme.palette.info.main, 0.1);
  const usersBadgeText = isDark ? '#90caf9' : theme.palette.info.dark;

  const planBadgeColors: Record<string, { bg: string; text: string }> = {
    enterprise: { bg: '#b8860b', text: '#fff' },
    pro:        { bg: '#1565c0', text: '#fff' },
    growth:     { bg: '#ef6c00', text: '#fff' },
    starter:    { bg: '#2e7d32', text: '#fff' },
  };
  const badge = planBadgeColors[currentPlanKey] || planBadgeColors.starter;

  const planStart = company.commitmentStartDate || activePlan?.startedAt;
  const planEnd = company.commitmentEndDate || activePlan?.endsAt;
  const planDaysLeft = planEnd
    ? Math.max(0, Math.ceil((new Date(planEnd).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : 0;
  const planExpired = planEnd ? new Date(planEnd) < new Date() : false;
  const planDaysColor = '#fff';
  const planDaysBg = planDaysLeft > 30 ? '#388e3c' : planDaysLeft > 7 ? '#ef6c00' : '#d32f2f';

  return (
    <Paper
      elevation={isDark ? 4 : 2}
      sx={{ p: { xs: 2, sm: 3 }, mb: 4, borderRadius: 2, bgcolor: panelBg, color: panelText }}
    >
      <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h5" fontWeight={600} sx={{ color: panelText, fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>
          Current Plan
        </Typography>
        <Chip
          label={currentPlanDef?.label || currentPlanKey.charAt(0).toUpperCase() + currentPlanKey.slice(1)}
          variant="filled"
          size="medium"
          icon={<CheckCircleOutline sx={{ color: `${badge.text} !important` }} />}
          sx={{ bgcolor: badge.bg, color: badge.text, fontWeight: 700 }}
        />
        {hasAnnualCommitment && (
          <Chip label="ANNUAL COMMITMENT" variant="outlined" size="small"
            sx={{ color: commitChipColor, borderColor: commitChipColor }}
          />
        )}
      </Stack>

      <Divider sx={{ my: 2, borderColor: dividerColor }} />

      {/* Plan-Level Dates & Subscription Actions */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 }, mb: 2.5, borderRadius: 1.5,
          bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : alpha(theme.palette.primary.main, 0.03),
          borderColor: isDark ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.primary.main, 0.15),
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="flex-start"
          useFlexGap flexWrap="wrap"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonth sx={{ fontSize: 18, color: panelTextSecondary }} />
            <Typography variant="body2" sx={{ color: panelTextSecondary }}>
              <strong>Start:</strong> {planStart ? formatDate(planStart) : 'N/A'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonth sx={{ fontSize: 18, color: panelTextSecondary }} />
            <Typography variant="body2" sx={{ color: panelTextSecondary }}>
              <strong>End:</strong> {planEnd ? formatDate(planEnd) : 'N/A'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <HourglassBottom sx={{ fontSize: 18, color: planDaysBg }} />
            <Chip
              label={planExpired ? 'Expired' : `${planDaysLeft} day${planDaysLeft !== 1 ? 's' : ''} left`}
              size="small"
              sx={{ bgcolor: planDaysBg, color: planDaysColor, fontWeight: 700, fontSize: '0.75rem' }}
            />
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5, borderColor: isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06) }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          useFlexGap flexWrap="wrap"
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <AutorenewOutlined sx={{ fontSize: 18, color: panelTextSecondary }} />
            <Typography variant="body2" sx={{ color: panelTextSecondary }}>
              <strong>Next Renewal:</strong> {nextRenewalDate}
            </Typography>
            <Chip
              label={isAnnual ? 'Annual' : 'Monthly'}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', fontWeight: 600, height: 20, color: commitChipColor, borderColor: commitChipColor }}
            />
          </Stack>

          {autoRenewalCancelled ? (
            <Button
              size="small" variant="outlined" color="success" fullWidth={false}
              startIcon={<AutorenewOutlined sx={{ fontSize: 16 }} />}
              onClick={onShowRenewConfirm}
              sx={{
                fontSize: '0.75rem', textTransform: 'none', whiteSpace: 'nowrap',
                alignSelf: { xs: 'stretch', sm: 'center' },
                animation: 'renewGlow 2s ease-in-out infinite',
                '@keyframes renewGlow': {
                  '0%, 100%': {
                    boxShadow: `0 0 4px ${alpha(theme.palette.success.main, 0.4)}`,
                    borderColor: theme.palette.success.main,
                  },
                  '50%': {
                    boxShadow: `0 0 14px ${alpha(theme.palette.success.main, 0.7)}, 0 0 28px ${alpha(theme.palette.success.main, 0.3)}`,
                    borderColor: theme.palette.success.light,
                  },
                },
              }}
            >
              Renew Subscription
            </Button>
          ) : (
            <Button
              size="small" variant="outlined" color="error" fullWidth={false}
              startIcon={<CancelOutlined sx={{ fontSize: 16 }} />}
              onClick={onShowCancelConfirm}
              sx={{ fontSize: '0.75rem', textTransform: 'none', whiteSpace: 'nowrap', alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              Cancel Subscription
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Module Accordions */}
      {company.productPlans
        .filter((p: ProductPlan) => p.status?.toLowerCase() === 'active')
        .map((plan: ProductPlan, idx: number) => {
          const configRows = buildConfigRows(plan);
          const isPlanExpired = plan.endsAt && new Date(plan.endsAt) < new Date();
          const statusLabel = isPlanExpired ? 'EXPIRED' : (plan.status?.toUpperCase() || 'ACTIVE');
          const statusBgColor = isPlanExpired ? '#d32f2f' : '#388e3c';
          const daysLeft = plan.endsAt
            ? Math.max(0, Math.ceil((new Date(plan.endsAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
            : 0;
          const daysChipText = '#fff';
          const daysChipBg = daysLeft > 30 ? '#388e3c' : daysLeft > 7 ? '#ef6c00' : '#d32f2f';

          return (
            <Accordion
              key={plan._id || idx}
              defaultExpanded={false}
              sx={{
                bgcolor: accordionBg, color: panelText,
                borderRadius: '8px !important', mb: 1.5,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: '0 0 12px 0' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: panelTextSecondary }} />}
                sx={{
                  minHeight: { xs: 48, sm: 56 }, px: { xs: 1.5, sm: 2 },
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center', gap: { xs: 0.75, sm: 1.5 },
                    flexWrap: 'wrap', rowGap: 0.75,
                  },
                }}
              >
                <Typography variant="body1" fontWeight={700}
                  sx={{ color: panelText, fontSize: { xs: '0.85rem', sm: '1rem' }, mr: { xs: 0.5, sm: 0 } }}
                >
                  {plan.module}
                </Typography>
                <Chip label={plan.licenseType} size="small" variant="outlined"
                  sx={{ color: licenseChipColor, borderColor: licenseChipColor, fontWeight: 600,
                    fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: { xs: 22, sm: 24 } }}
                />
                <Chip label={statusLabel} size="small"
                  sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                    bgcolor: statusBgColor, color: '#fff',
                    fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: { xs: 22, sm: 24 } }}
                />
                <Chip label={`${plan.noOfUsers} user${plan.noOfUsers > 1 ? 's' : ''}`} size="small"
                  sx={{ ml: { xs: 0, sm: 'auto' }, mr: { xs: 0, sm: 1 },
                    bgcolor: usersBadgeBg, color: usersBadgeText, fontWeight: 600,
                    fontSize: { xs: '0.6rem', sm: '0.7rem' }, height: { xs: 22, sm: 24 } }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: { xs: 1.5, sm: 2 } }}>
                <Paper variant="outlined"
                  sx={{
                    p: { xs: 1, sm: 1.5 }, mb: 2, borderRadius: 1.5,
                    bgcolor: isDark ? alpha(theme.palette.common.white, 0.04) : alpha(theme.palette.primary.main, 0.03),
                    borderColor: isDark ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.primary.main, 0.15),
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="flex-start"
                    useFlexGap flexWrap="wrap"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CalendarMonth sx={{ fontSize: 18, color: panelTextSecondary }} />
                      <Typography variant="body2" sx={{ color: panelTextSecondary }}>
                        <strong>Start:</strong> {formatDate(plan.startedAt)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CalendarMonth sx={{ fontSize: 18, color: panelTextSecondary }} />
                      <Typography variant="body2" sx={{ color: panelTextSecondary }}>
                        <strong>End:</strong> {formatDate(plan.endsAt)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <HourglassBottom sx={{ fontSize: 18, color: daysChipBg }} />
                      <Chip
                        label={isPlanExpired ? 'Expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                        size="small"
                        sx={{ bgcolor: daysChipBg, color: daysChipText, fontWeight: 700, fontSize: '0.75rem' }}
                      />
                    </Stack>
                  </Stack>
                </Paper>

                {configRows.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: tableHeaderBg }}>
                          <TableCell sx={{ fontWeight: 600, color: panelTextSecondary, borderColor: tableBorder }}>Setting</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: panelTextSecondary, borderColor: tableBorder }}>Limit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {configRows.map((row, ri) => (
                          <TableRow key={ri} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ color: panelText, borderColor: tableBorderLight }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2">{row.icon}</Typography>
                                <Typography variant="body2" sx={{ color: panelText }}>{row.label}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ color: panelText, borderColor: tableBorderLight }}>
                              <Typography variant="body2" fontWeight={500} sx={{ color: valueColor }}>{row.value}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" sx={{ color: panelTextSecondary, fontStyle: 'italic', py: 1 }}>
                    No additional quota configurations for this module.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}

      {company.productPlans.filter((p: ProductPlan) => p.status?.toLowerCase() === 'active').length === 0 && (
        <Typography variant="body2" sx={{ color: panelTextSecondary, fontStyle: 'italic' }}>
          No active product plans found.
        </Typography>
      )}
    </Paper>
  );
};
